import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

const translations = {
  es: {
    back: 'Volver',
    backToHome: 'Volver al inicio',
    title: 'Términos y Condiciones',
    section1: '1. OBJETO',
    section1Text: 'Las presentes condiciones generales de uso regulan los términos y condiciones de acceso y uso de KeyPubli, propiedad de KeyPubli S.L. El usuario deberá aceptar estas condiciones para usar todos los servicios e información que se facilitan desde el portal.',
    section2: '2. CONDICIONES DE ACCESO Y UTILIZACIÓN',
    section2Text: 'El acceso a la página web no otorga al usuario ningún derecho de propiedad sobre los mismos.',
    section2List: 'El usuario del portal se compromete a:',
    condition1: 'Hacer un uso lícito del portal de conformidad con la legislación vigente.',
    condition2: 'Respetar las condiciones de uso del portal.',
    condition3: 'No utilizar el portal para fines ilícitos o lesivos contra terceros.',
    condition4: 'No dañar, inutilizar, sobrecargar o deteriorar el portal.',
    section3: '3. CONTENIDOS',
    section3Text: 'Los contenidos de la página web se proporcionan "tal cual" y la empresa no garantiza que estén actualizados, completos, correctos o libres de errores.',
    section4: '4. PROPIEDAD INTELECTUAL E INDUSTRIAL',
    section4Text: 'Todos los contenidos del portal son propiedad intelectual de la empresa, sin que puedan entenderse cedidos al usuario ninguno de los derechos de explotación.',
    section5: '5. SERVICIOS DE LA PLATAFORMA',
    section5Text: 'KeyPubli es un marketplace de enlaces para estrategias SEO que ofrece:',
    service1: 'Catálogo de medios y sitios web para publicación de contenido patrocinado.',
    service2: 'Información detallada sobre métricas SEO (DR, tráfico, categorías).',
    service3: 'Herramientas de filtrado y búsqueda avanzada.',
    service4: 'Sistema de gestión de pedidos y facturación.',
    section6: '6. PRECIOS Y PAGOS',
    section6Text: 'Los precios mostrados en la plataforma no incluyen IVA salvo que se indique lo contrario. La empresa se reserva el derecho de modificar los precios en cualquier momento.',
    section7: '7. EXCLUSIÓN DE GARANTÍAS Y RESPONSABILIDAD',
    section7Text: 'La empresa no se hace responsable de los daños y perjuicios de cualquier naturaleza que pudieran ocasionar.',
    section8: '8. ENLACES',
    section8Text: 'La empresa no ejercerá ningún tipo de control sobre sitios de terceros enlazados y no asumirá responsabilidad alguna por sus contenidos.',
    section9: '9. MODIFICACIONES',
    section9Text: 'La empresa se reserva el derecho de efectuar sin previo aviso las modificaciones que considere oportunas en su portal.',
    section10: '10. DERECHO DE EXCLUSIÓN',
    section10Text: 'La empresa se reserva el derecho a denegar o retirar el acceso al portal sin necesidad de preaviso.',
    section11: '11. GENERALIDADES',
    section11Text: 'La empresa perseguirá el incumplimiento de las presentes condiciones ejerciendo todas las acciones civiles y penales que correspondan.',
    section12: '12. LEGISLACIÓN APLICABLE',
    section12Text: 'La relación entre la empresa y el usuario se regirá por la normativa española vigente. Todas las disputas se resolverán por los juzgados y tribunales españoles.'
  },
  en: {
    back: 'Back',
    backToHome: 'Back to home',
    title: 'Terms and Conditions',
    section1: '1. PURPOSE',
    section1Text: 'These general terms of use regulate the terms and conditions of access and use of KeyPubli, owned by KeyPubli S.L. The user must accept these conditions to use all services and information provided through the portal.',
    section2: '2. ACCESS AND USE CONDITIONS',
    section2Text: 'Access to the website does not grant the user any property rights over it.',
    section2List: 'The portal user agrees to:',
    condition1: 'Make lawful use of the portal in accordance with current legislation.',
    condition2: 'Respect the portal\'s terms of use.',
    condition3: 'Not use the portal for illegal purposes or harmful to third parties.',
    condition4: 'Not damage, disable, overload or impair the portal.',
    section3: '3. CONTENTS',
    section3Text: 'The contents of the website are provided "as is" and the company does not guarantee that they are updated, complete, correct or error-free.',
    section4: '4. INTELLECTUAL AND INDUSTRIAL PROPERTY',
    section4Text: 'All portal contents are the intellectual property of the company, without any exploitation rights being transferred to the user.',
    section5: '5. PLATFORM SERVICES',
    section5Text: 'KeyPubli is a link marketplace for SEO strategies that offers:',
    service1: 'Catalog of media and websites for sponsored content publication.',
    service2: 'Detailed information on SEO metrics (DR, traffic, categories).',
    service3: 'Advanced filtering and search tools.',
    service4: 'Order management and billing system.',
    section6: '6. PRICES AND PAYMENTS',
    section6Text: 'Prices shown on the platform do not include VAT unless otherwise indicated. The company reserves the right to modify prices at any time.',
    section7: '7. DISCLAIMER OF WARRANTIES AND LIABILITY',
    section7Text: 'The company is not responsible for any damages that may be caused.',
    section8: '8. LINKS',
    section8Text: 'The company will not exercise any control over linked third-party sites and will not assume any responsibility for their contents.',
    section9: '9. MODIFICATIONS',
    section9Text: 'The company reserves the right to make modifications to its portal without prior notice.',
    section10: '10. RIGHT OF EXCLUSION',
    section10Text: 'The company reserves the right to deny or withdraw access to the portal without prior notice.',
    section11: '11. GENERAL PROVISIONS',
    section11Text: 'The company will pursue any breach of these conditions by exercising all applicable civil and criminal actions.',
    section12: '12. APPLICABLE LAW',
    section12Text: 'The relationship between the company and the user will be governed by current Spanish law. All disputes will be resolved by Spanish courts and tribunals.'
  },
  de: {
    back: 'Zurück',
    backToHome: 'Zurück zur Startseite',
    title: 'Allgemeine Geschäftsbedingungen',
    section1: '1. ZWECK',
    section1Text: 'Diese allgemeinen Nutzungsbedingungen regeln die Bedingungen für den Zugang und die Nutzung von KeyPubli, Eigentum von KeyPubli S.L. Der Benutzer muss diese Bedingungen akzeptieren.',
    section2: '2. ZUGANGS- UND NUTZUNGSBEDINGUNGEN',
    section2Text: 'Der Zugang zur Website gewährt dem Benutzer keine Eigentumsrechte.',
    section2List: 'Der Portal-Benutzer verpflichtet sich:',
    condition1: 'Das Portal rechtmäßig gemäß geltendem Recht zu nutzen.',
    condition2: 'Die Nutzungsbedingungen des Portals zu respektieren.',
    condition3: 'Das Portal nicht für illegale oder schädliche Zwecke zu nutzen.',
    condition4: 'Das Portal nicht zu beschädigen, zu deaktivieren, zu überlasten oder zu beeinträchtigen.',
    section3: '3. INHALTE',
    section3Text: 'Die Inhalte der Website werden "wie sie sind" bereitgestellt und das Unternehmen garantiert nicht, dass sie aktuell, vollständig, korrekt oder fehlerfrei sind.',
    section4: '4. GEISTIGES UND GEWERBLICHES EIGENTUM',
    section4Text: 'Alle Portal-Inhalte sind geistiges Eigentum des Unternehmens, ohne dass dem Benutzer Verwertungsrechte übertragen werden.',
    section5: '5. PLATTFORM-DIENSTE',
    section5Text: 'KeyPubli ist ein Link-Marktplatz für SEO-Strategien, der bietet:',
    service1: 'Katalog von Medien und Websites für gesponserte Inhalte.',
    service2: 'Detaillierte Informationen zu SEO-Metriken (DR, Traffic, Kategorien).',
    service3: 'Erweiterte Filter- und Suchwerkzeuge.',
    service4: 'Bestell- und Abrechnungssystem.',
    section6: '6. PREISE UND ZAHLUNGEN',
    section6Text: 'Die auf der Plattform angezeigten Preise enthalten keine MwSt., sofern nicht anders angegeben.',
    section7: '7. HAFTUNGSAUSSCHLUSS',
    section7Text: 'Das Unternehmen haftet nicht für Schäden jeglicher Art.',
    section8: '8. LINKS',
    section8Text: 'Das Unternehmen übt keine Kontrolle über verlinkte Drittseiten aus.',
    section9: '9. ÄNDERUNGEN',
    section9Text: 'Das Unternehmen behält sich das Recht vor, Änderungen ohne vorherige Ankündigung vorzunehmen.',
    section10: '10. AUSSCHLUSSRECHT',
    section10Text: 'Das Unternehmen behält sich das Recht vor, den Zugang ohne vorherige Ankündigung zu verweigern.',
    section11: '11. ALLGEMEINES',
    section11Text: 'Das Unternehmen wird Verstöße gegen diese Bedingungen verfolgen.',
    section12: '12. ANWENDBARES RECHT',
    section12Text: 'Die Beziehung zwischen Unternehmen und Benutzer unterliegt spanischem Recht.'
  },
  fr: {
    back: 'Retour',
    backToHome: 'Retour à l\'accueil',
    title: 'Conditions Générales',
    section1: '1. OBJET',
    section1Text: 'Les présentes conditions générales d\'utilisation régissent les termes et conditions d\'accès et d\'utilisation de KeyPubli, propriété de KeyPubli S.L. L\'utilisateur doit accepter ces conditions.',
    section2: '2. CONDITIONS D\'ACCÈS ET D\'UTILISATION',
    section2Text: 'L\'accès au site web ne confère à l\'utilisateur aucun droit de propriété.',
    section2List: 'L\'utilisateur du portail s\'engage à:',
    condition1: 'Faire un usage licite du portail conformément à la législation en vigueur.',
    condition2: 'Respecter les conditions d\'utilisation du portail.',
    condition3: 'Ne pas utiliser le portail à des fins illicites ou nuisibles à des tiers.',
    condition4: 'Ne pas endommager, désactiver, surcharger ou détériorer le portail.',
    section3: '3. CONTENUS',
    section3Text: 'Les contenus du site web sont fournis "tels quels" et l\'entreprise ne garantit pas qu\'ils soient à jour, complets, corrects ou sans erreur.',
    section4: '4. PROPRIÉTÉ INTELLECTUELLE ET INDUSTRIELLE',
    section4Text: 'Tous les contenus du portail sont la propriété intellectuelle de l\'entreprise.',
    section5: '5. SERVICES DE LA PLATEFORME',
    section5Text: 'KeyPubli est une marketplace de liens pour les stratégies SEO qui offre:',
    service1: 'Catalogue de médias et sites web pour la publication de contenu sponsorisé.',
    service2: 'Informations détaillées sur les métriques SEO (DR, trafic, catégories).',
    service3: 'Outils de filtrage et de recherche avancés.',
    service4: 'Système de gestion des commandes et de facturation.',
    section6: '6. PRIX ET PAIEMENTS',
    section6Text: 'Les prix affichés sur la plateforme n\'incluent pas la TVA sauf indication contraire.',
    section7: '7. EXCLUSION DE GARANTIES ET RESPONSABILITÉ',
    section7Text: 'L\'entreprise n\'est pas responsable des dommages de quelque nature que ce soit.',
    section8: '8. LIENS',
    section8Text: 'L\'entreprise n\'exerce aucun contrôle sur les sites tiers liés.',
    section9: '9. MODIFICATIONS',
    section9Text: 'L\'entreprise se réserve le droit d\'apporter des modifications sans préavis.',
    section10: '10. DROIT D\'EXCLUSION',
    section10Text: 'L\'entreprise se réserve le droit de refuser l\'accès sans préavis.',
    section11: '11. GÉNÉRALITÉS',
    section11Text: 'L\'entreprise poursuivra toute violation de ces conditions.',
    section12: '12. DROIT APPLICABLE',
    section12Text: 'La relation entre l\'entreprise et l\'utilisateur est régie par le droit espagnol.'
  },
  it: {
    back: 'Indietro',
    backToHome: 'Torna alla home',
    title: 'Termini e Condizioni',
    section1: '1. OGGETTO',
    section1Text: 'Le presenti condizioni generali di utilizzo regolano i termini e le condizioni di accesso e utilizzo di KeyPubli, di proprietà di KeyPubli S.L. L\'utente deve accettare queste condizioni.',
    section2: '2. CONDIZIONI DI ACCESSO E UTILIZZO',
    section2Text: 'L\'accesso al sito web non conferisce all\'utente alcun diritto di proprietà.',
    section2List: 'L\'utente del portale si impegna a:',
    condition1: 'Fare un uso lecito del portale in conformità con la legislazione vigente.',
    condition2: 'Rispettare le condizioni di utilizzo del portale.',
    condition3: 'Non utilizzare il portale per scopi illeciti o dannosi per terzi.',
    condition4: 'Non danneggiare, disabilitare, sovraccaricare o deteriorare il portale.',
    section3: '3. CONTENUTI',
    section3Text: 'I contenuti del sito web sono forniti "così come sono" e l\'azienda non garantisce che siano aggiornati, completi, corretti o privi di errori.',
    section4: '4. PROPRIETÀ INTELLETTUALE E INDUSTRIALE',
    section4Text: 'Tutti i contenuti del portale sono proprietà intellettuale dell\'azienda.',
    section5: '5. SERVIZI DELLA PIATTAFORMA',
    section5Text: 'KeyPubli è un marketplace di link per strategie SEO che offre:',
    service1: 'Catalogo di media e siti web per la pubblicazione di contenuti sponsorizzati.',
    service2: 'Informazioni dettagliate sulle metriche SEO (DR, traffico, categorie).',
    service3: 'Strumenti di filtraggio e ricerca avanzati.',
    service4: 'Sistema di gestione degli ordini e fatturazione.',
    section6: '6. PREZZI E PAGAMENTI',
    section6Text: 'I prezzi mostrati sulla piattaforma non includono l\'IVA salvo diversa indicazione.',
    section7: '7. ESCLUSIONE DI GARANZIE E RESPONSABILITÀ',
    section7Text: 'L\'azienda non è responsabile per danni di qualsiasi natura.',
    section8: '8. COLLEGAMENTI',
    section8Text: 'L\'azienda non esercita alcun controllo sui siti di terze parti collegati.',
    section9: '9. MODIFICHE',
    section9Text: 'L\'azienda si riserva il diritto di apportare modifiche senza preavviso.',
    section10: '10. DIRITTO DI ESCLUSIONE',
    section10Text: 'L\'azienda si riserva il diritto di negare l\'accesso senza preavviso.',
    section11: '11. GENERALITÀ',
    section11Text: 'L\'azienda perseguirà qualsiasi violazione di queste condizioni.',
    section12: '12. LEGGE APPLICABILE',
    section12Text: 'Il rapporto tra l\'azienda e l\'utente è regolato dalla legge spagnola.'
  }
};

export const TermsPage = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.es;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <Link to="/"><Button variant="ghost" className="gap-2"><ArrowLeft className="h-4 w-4" />{t.back}</Button></Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
          </div>

          <div className="prose prose-gray max-w-none">
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section1}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{t.section1Text}</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section2}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">{t.section2Text}</p>
            <p className="text-gray-700 leading-relaxed mb-2">{t.section2List}</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
              <li>{t.condition1}</li>
              <li>{t.condition2}</li>
              <li>{t.condition3}</li>
              <li>{t.condition4}</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section3}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{t.section3Text}</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section4}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{t.section4Text}</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section5}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">{t.section5Text}</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
              <li>{t.service1}</li>
              <li>{t.service2}</li>
              <li>{t.service3}</li>
              <li>{t.service4}</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section6}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{t.section6Text}</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section7}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{t.section7Text}</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section8}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{t.section8Text}</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section9}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{t.section9Text}</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section10}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{t.section10Text}</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section11}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{t.section11Text}</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">{t.section12}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{t.section12Text}</p>
          </div>

          <div className="mt-12 pt-6 border-t">
            <Link to="/"><Button className="gap-2"><ArrowLeft className="h-4 w-4" />{t.backToHome}</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
